import { format, isValid, parseISO } from "date-fns";

export function formatCurrency(value: number | string | null | undefined): string {
  const amount = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number.isFinite(amount) ? amount : 0,
  );
}

export function formatNumber(value: number | string | null | undefined): string {
  const amount = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("de-DE").format(Number.isFinite(amount) ? amount : 0);
}

export function formatPercent(value: number | string | null | undefined): string {
  const amount = typeof value === "number" ? value : Number(value ?? 0);
  return `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(
    Number.isFinite(amount) ? amount : 0,
  )} %`;
}

export function formatDate(value: string | null | undefined, fallback = "–"): string {
  if (!value) return fallback;
  const parsed = parseISO(value);
  return isValid(parsed)
    ? new Intl.DateTimeFormat("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(parsed)
    : fallback;
}

export function formatDateTime(value: string | null | undefined, fallback = "–"): string {
  if (!value) return fallback;
  const parsed = parseISO(value);
  return isValid(parsed)
    ? new Intl.DateTimeFormat("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(parsed)
    : fallback;
}

/** DATE values are calendar days; timestamps are shown in the user's local day. */
export function dateInputValue(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : "";
}

export function dateTimeInputValue(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = parseISO(value);
  if (!isValid(parsed)) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(parsed)
      .map(({ type, value }) => [type, value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "EH"
  );
}

const labels: Record<string, string> = {
  active: "Aktiv",
  inactive: "Inaktiv",
  lead: "Interessent",
  customer: "Kunde",
  prospect: "Potenzieller Kunde",
  new: "Neu",
  contacted: "Kontaktiert",
  unqualified: "Nicht qualifiziert",
  converted: "Konvertiert",
  qualified: "Qualifiziert",
  proposal: "Angebot",
  negotiation: "Verhandlung",
  won: "Gewonnen",
  lost: "Verloren",
  draft: "Entwurf",
  review: "In Prüfung",
  approved: "Freigegeben",
  scheduled: "Geplant",
  sent: "Versendet",
  logged: "Dokumentiert",
  received: "Empfangen",
  confirmed: "Bestätigt",
  cancelled: "Storniert",
  final: "Final",
  simulated: "Simuliert",
  published: "Veröffentlicht",
  archived: "Archiviert",
  planned: "Geplant",
  in_progress: "In Arbeit",
  inprogress: "In Arbeit",
  at_risk: "Gefährdet",
  blocked: "Blockiert",
  completed: "Abgeschlossen",
  done: "Erledigt",
  open: "Offen",
  todo: "Offen",
  overdue: "Überfällig",
  paid: "Bezahlt",
  partial: "Teilbezahlt",
  stopped: "Gestoppt",
  pending: "Ausstehend",
  enrolled: "Eingeschrieben",
  passed: "Bestanden",
  failed: "Nicht bestanden",
  anonymous: "Anonym",
  personalized: "Personalisiert",
  pseudonymous: "Pseudonymisiert",
  connected: "Verbunden",
  configured: "Konfiguriert",
  not_configured: "Nicht konfiguriert",
  verified: "Geprüft",
  disconnected: "Nicht verbunden",
  healthy: "Bereit",
  urgent: "Dringend",
  granted: "Erteilt",
  revoked: "Widerrufen",
  opted_in: "Einwilligung liegt vor",
  opted_out: "Abgemeldet",
  unknown: "Ungeklärt",
  success: "Erfolgreich",
  failure: "Fehlgeschlagen",
  denied: "Abgelehnt",
  routed: "Zugewiesen",
  duplicate: "Mögliche Dublette",
  responded: "Beantwortet",
  queued: "Eingeplant",
  processing: "In Verarbeitung",
  succeeded: "Erfolgreich",
  opened: "Geöffnet",
  accepted: "Angenommen",
  rejected: "Abgelehnt",
  expired: "Abgelaufen",
  awaiting_approval: "Freigabe ausstehend",
  signing: "In Unterzeichnung",
  signed: "Unterzeichnet",
  waiting: "Wartet",
  paused: "Pausiert",
  resolved: "Gelöst",
  closed: "Geschlossen",
  met: "Eingehalten",
  warning: "Warnung",
  breached: "Verletzt",
  offered: "Angeboten",
  declined: "Abgelehnt",
  applied: "Übernommen",
  deviated: "Lokal abweichend",
  low: "Niedrig",
  normal: "Normal",
  high: "Hoch",
};

export function statusLabel(status: string | null | undefined): string {
  if (!status) return "Ohne Status";
  const key = status.toLowerCase().replace(/[\s-]+/g, "_");
  return labels[key] ?? status;
}

export function statusTone(
  status: string | null | undefined,
): "neutral" | "success" | "warning" | "danger" | "info" | "brand" {
  const key = (status ?? "").toLowerCase().replace(/[\s-]+/g, "_");
  if (
    [
      "active",
      "won",
      "approved",
      "sent",
      "published",
      "completed",
      "done",
      "paid",
      "passed",
      "connected",
      "verified",
      "healthy",
      "converted",
      "succeeded",
      "accepted",
      "signed",
      "resolved",
      "closed",
      "met",
      "applied",
      "responded",
      "received",
      "confirmed",
      "final",
    ].includes(key)
  )
    return "success";
  if (
    [
      "overdue",
      "at_risk",
      "blocked",
      "failed",
      "lost",
      "disconnected",
      "breached",
      "rejected",
      "expired",
      "cancelled",
    ].includes(key)
  )
    return "danger";
  if (
    [
      "pending",
      "planned",
      "scheduled",
      "partial",
      "review",
      "proposal",
      "negotiation",
      "awaiting_approval",
      "warning",
      "waiting",
      "paused",
    ].includes(key)
  )
    return "warning";
  if (
    [
      "new",
      "contacted",
      "active",
      "in_progress",
      "inprogress",
      "enrolled",
      "qualified",
      "routed",
      "queued",
      "processing",
      "opened",
      "signing",
      "offered",
      "logged",
      "configured",
    ].includes(key)
  )
    return "info";
  if (["customer", "anonymous"].includes(key)) return "brand";
  return "neutral";
}

const roleLabels: Record<string, string> = {
  system_admin: "Systemadministration",
  platform_admin: "Systemadministration",
  group_management: "Geschäftsführung",
  group_admin: "Zentrale Administration",
  group_operations: "Zentrale Steuerung",
  tenant_admin: "Mandantenadministration",
  sales: "Vertrieb",
  project_manager: "Projektleitung",
  project_member: "Projektteam",
  project: "Projektteam",
  marketing: "Marketing",
  trainer: "Trainer",
  instructor: "Trainer",
  learning_admin: "Lernredaktion",
  survey_admin: "Befragungsadministration",
  survey_analyst: "Befragungsauswertung",
  finance: "Finanzen",
  customer_admin: "Kundenadministration",
  learner: "Lernende Person",
  user: "Nutzer",
  admin: "Administration",
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return "Nutzer";
  const key = role.toLowerCase().replace(/[\s-]+/g, "_");
  return roleLabels[key] ?? role;
}

/** A datetime-local value always means the customer's office timezone. */
export function berlinDateTimeToIso(value: string): string {
  const wallTime = new Date(`${value}Z`);
  let result = wallTime;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const rendered = dateTimeInputValue(result.toISOString());
    const difference = new Date(`${value}Z`).getTime() - new Date(`${rendered}Z`).getTime();
    if (difference === 0) return result.toISOString();
    result = new Date(result.getTime() + difference);
  }
  throw new Error(
    "Diese Uhrzeit existiert wegen der Zeitumstellung nicht. Bitte wählen Sie eine andere Uhrzeit.",
  );
}

/** Preserve unchanged timestamps, including seconds, when editing minute-based fields. */
export function dateTimeInputToIso(value: string, original?: string | null): string | null {
  if (!value) return null;
  if (original && value === dateTimeInputValue(original)) return original;
  return berlinDateTimeToIso(value);
}
